import { useAtomValue } from "jotai";
import qs from "qs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { useNewsletterApi, useSignedUpNewsletter } from "scmp-sdk";

import { useFallbackStorageValue, useParsedCookie } from "shared/hooks";
import { accountAtom } from "shared/lib/account";

import { useLoginDialogStateHelper } from "scmp-app/components/login-dialog/hooks";
import { SignUpMatrixV1LocalStorageKey } from "scmp-app/components/popup-container/sign-up-action-redirect/consts";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { useNewsletterLandingContext } from "scmp-app/pages/newsletters/contexts";
import { getPymChild } from "scmp-app/pages/newsletters/hooks";
import { useNewsletterPreviewContext } from "scmp-app/pages/newsletters/preview/contexts";
import type { signUpButtonNewsletter$key } from "scmp-app/queries/__generated__/signUpButtonNewsletter.graphql";

import { withFallback } from "./hoc";
import IconEmail from "./icon-email.svg";
import IconTick from "./icon-tick.svg";
import { Button, DottySpan } from "./styles";

export type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  displayText?: string;
  packageName?: string;
  reference: signUpButtonNewsletter$key;
};

const Component: React.FunctionComponent<Props> = ({
  displayText = "SIGN UP",
  packageName,
  reference,
  ...props
}) => {
  const landingContext = useNewsletterLandingContext();
  const previewContext = useNewsletterPreviewContext();

  const triggerPoint =
    landingContext.ga4CustomParameter?.triggerPoint ??
    previewContext.ga4CustomParameter?.triggerPoint;

  const newsletters = useFragment(
    graphql`
      fragment signUpButtonNewsletter on Newsletter @relay(plural: true) {
        entityId
        name
        ...entityLink
      }
    `,
    reference,
  );

  const { isLoggedIn, user } = useAtomValue(accountAtom);
  const { openLoginDialog } = useLoginDialogStateHelper();

  const { handleSubscribeNewsletter } = useNewsletterApi();
  const signedUpNewsletterIds = useSignedUpNewsletter();

  const allNewsletterIds = useMemo(
    () => newsletters.map(newsletter => newsletter.entityId),
    [newsletters],
  );
  const isNotSignedUp = useMemo(
    () => allNewsletterIds.some(newsletterId => !signedUpNewsletterIds?.includes(newsletterId)),
    [allNewsletterIds, signedUpNewsletterIds],
  );

  const { value: dataMatrixInLocalStorage } = useFallbackStorageValue<Record<string, unknown>>(
    SignUpMatrixV1LocalStorageKey,
    { defaultValue: {} },
  );

  const { setUnsubscribePayload } = useNewsletterLandingContext();
  const { value: loginType } = useParsedCookie("scmp_user_login_type", raw => raw);

  const [isLoading, setIsLoading] = useState(false);
  const [isJustSubscribed, setIsJustSubscribed] = useState(false);

  useEffect(() => {
    if (isNotSignedUp) return;
    setIsJustSubscribed(false);
  }, [isNotSignedUp]);

  const handleOnSignUpClick = useCallback(async () => {
    if (isLoading) return;
    if (!isNotSignedUp && isLoggedIn && user?.useremail) {
      getPymChild().sendMessage(
        "SHOW_UNSUBSCRIBE_MODAL",
        JSON.stringify({
          title: `Are you sure you want to unsubscribe <strong>${newsletters.map(newsletter => newsletter.name).join(", ")}</strong>`,
        }),
      );
      setUnsubscribePayload?.({
        dataMatrix: dataMatrixInLocalStorage,
        email: user.useremail,
        newsletterIds: allNewsletterIds,
        newsletterNames: newsletters.map(newsletter => newsletter.name),
      });
      return;
    }

    sendGA4Tracking({
      action: "click",
      category: "nl",
      customized_parameters: {
        method: (loginType ?? "").toLowerCase(),
        newsletter_name: newsletters.map(({ name }) => name).join("|"),
        ...(triggerPoint && { trigger_point: triggerPoint }),
        ...(packageName && { package_name: packageName }),
      },
      subcategory: "sign_up",
    });

    if (isLoggedIn && user?.useremail) {
      setIsLoading(true);

      const response = await handleSubscribeNewsletter(allNewsletterIds, user?.useremail);

      sendGA4Tracking({
        action: "sys",
        category: "nl",
        customized_parameters: {
          method: (loginType ?? "").toLowerCase(),
          newsletter_name: newsletters.map(({ name }) => name).join("|"),
          ...(triggerPoint && { trigger_point: triggerPoint }),
          ...(packageName && { package_name: packageName }),
        },
        subcategory: "sign_up",
      });

      setIsLoading(false);

      if (!response?.errors?.length) {
        setIsJustSubscribed(true);
      }

      return;
    }

    const redirectUrl = new URL(location.href);
    redirectUrl.search = qs.stringify({
      newsletterIds: allNewsletterIds,
      newsletterName: newsletters.map(newsletter => newsletter.name).join("|"),
      subscription: "true",
      triggerPoint: triggerPoint ?? "",
      ...(packageName && { packageName }),
    });
    openLoginDialog({
      description:
        "Login in or register to sign up for our newsletters and get updates on the stories that matter to you.",
      destination: redirectUrl.toString(),
      ga4CustomParameter: {
        newsletter_name: newsletters.map(({ name }) => name).join("|"),
        trigger_point: "newsletter",
        ...(packageName && { package_name: packageName }),
      },
      title: "Stay informed with newsletters",
    });
  }, [
    isNotSignedUp,
    isLoggedIn,
    user?.useremail,
    newsletters,
    setUnsubscribePayload,
    dataMatrixInLocalStorage,
    allNewsletterIds,
    loginType,
    triggerPoint,
    packageName,
    handleSubscribeNewsletter,
    openLoginDialog,
    setIsLoading,
    isLoading,
  ]);

  const shouldShowSignedUp = useMemo(
    () => isJustSubscribed || !isNotSignedUp,
    [isJustSubscribed, isNotSignedUp],
  );

  const renderButtonContent = useMemo(() => {
    if (isLoading) {
      return <DottySpan>SENDING</DottySpan>;
    }

    return shouldShowSignedUp ? (
      <>
        <IconTick />
        SIGNED UP
      </>
    ) : (
      <>
        <IconEmail />
        {displayText}
      </>
    );
  }, [displayText, isLoading, shouldShowSignedUp]);

  return (
    <Button {...props} $isSignedUp={shouldShowSignedUp} onClick={handleOnSignUpClick}>
      {renderButtonContent}
    </Button>
  );
};

export const SignUpButton = withFallback(Component);
